<template>
  <div class="image-picker">
    <!-- Cropping replaces the picker in place rather than opening over it: the
         photo being framed is the thing the preview would have shown, and both
         hosts (profile sheet, list editor) are already overlays. -->
    <ImageCropper
      v-if="showCropper"
      v-model="showCropper"
      :src="pendingDataUrl"
      :aspect="cropAspect"
      :round="round"
      @crop="onCropped"
      @cancel="onCropCancelled"
    />

    <!-- hidePreview is for surfaces that already render the photo themselves
         (the profile header shows an AppAvatar). Without it the picker draws a
         second copy directly under the first. -->
    <div
      v-if="!hidePreview && !showCropper"
      class="image-picker-preview"
      :class="{ 'image-picker-preview--round': round }"
    >
      <!-- The local preview wins while an upload is in flight: nobody should
           watch a spinner to see their own photo. -->
      <img v-if="localPreview" :src="localPreview" :alt="alt" class="image-picker-img" />
      <AppImage
        v-else-if="modelValue"
        :image-id="modelValue"
        :avg-color="avgColor"
        :alt="alt"
        :aspect="round ? '1 / 1' : aspect"
        class="image-picker-img"
      />
      <div v-else class="image-picker-empty" :style="{ aspectRatio: round ? '1 / 1' : aspect }">
        <slot name="empty">
          <span class="image-picker-empty-icon" aria-hidden="true">{{ emptyIcon }}</span>
        </slot>
      </div>
      <div v-if="isUploading" class="image-picker-busy"><span class="spinner" /></div>
    </div>

    <div v-if="!showCropper" class="image-picker-actions" :class="{ 'image-picker-actions--links': links }">
      <AppButton :variant="links ? 'link' : 'outline'" size="sm" :disabled="isUploading" @click="pick">
        {{ modelValue || localPreview ? t("images.change") : t("images.add") }}
      </AppButton>
      <!-- The separator is decorative: it only reads as one caption line in the
           link layout, where the two actions sit at the same visual weight. -->
      <span v-if="links && (modelValue || localPreview)" class="image-picker-sep" aria-hidden="true">·</span>
      <AppButton
        v-if="modelValue || localPreview"
        variant="link"
        size="sm"
        :disabled="isUploading"
        @click="clear"
      >
        {{ t("common.remove") }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "../../composables/useToast"
import type { CaptureOptions, CropRect } from "../../services/imageCapture"
import { AVATAR_OPTIONS, COVER_OPTIONS, pickImage, prepareImage } from "../../services/imageCapture"
import type { ImagePurpose } from "../../services/imageService"
import { deleteImage, uploadImage } from "../../services/imageService"
import AppButton from "./AppButton.vue"
import AppImage from "./AppImage.vue"
import ImageCropper from "./ImageCropper.vue"

/**
 * Pick a photo, downscale it on the device, upload it, and hand back the image
 * id. Used for avatars, household photos and shopping list covers — the sizes
 * differ (256px square vs 1024px wide), nothing else does.
 */
const props = withDefaults(
  defineProps<{
    /** Current image id, or null. */
    modelValue?: string | null
    purpose: ImagePurpose
    avgColor?: string | null
    householdId?: string | null
    /** Circular crop + preview (avatars). */
    round?: boolean
    aspect?: string
    alt?: string
    emptyIcon?: string
    /** Delete the replaced image instead of leaving it for the GC sweep. */
    deleteReplaced?: boolean
    /** Render only the buttons — for surfaces that show the photo themselves. */
    hidePreview?: boolean
    /**
     * Both actions as one row of text links rather than an outline button plus
     * a link. Use where the photo is the subject and the controls are its
     * caption (the profile header) — an outline button there competes with the
     * card's real action.
     */
    links?: boolean
  }>(),
  { aspect: "3 / 1", alt: "", emptyIcon: "🖼️", deleteReplaced: true, hidePreview: false, links: false },
)

const emit = defineEmits<{
  "update:modelValue": [imageId: string | null]
  /** The colour to store alongside the id, so the placeholder survives a reload. */
  "update:avgColor": [color: string | null]
}>()

const { t } = useI18n()
const toast = useToast()

const isUploading = ref(false)
const localPreview = ref<string | null>(null)
const showCropper = ref(false)
const pendingDataUrl = ref<string | null>(null)

const captureOptions = computed<CaptureOptions>(() => (props.round ? AVATAR_OPTIONS : COVER_OPTIONS))
// The cropper frames to the same shape the encoder will cut to, so what the
// user lines up is exactly what gets stored.
const cropAspect = computed(() => captureOptions.value.aspect ?? 1)

async function pick() {
  if (isUploading.value) return
  try {
    const picked = await pickImage(captureOptions.value)
    if (!picked) return

    // Native already ran the OS editor; cropping again would ask for the same
    // decision twice. Web gets our cropper, unless the preset keeps the
    // original shape (receipts) and so has nothing to frame.
    if (picked.alreadyCropped || !captureOptions.value.aspect) {
      await encodeAndUpload(picked.dataUrl)
      return
    }
    pendingDataUrl.value = picked.dataUrl
    showCropper.value = true
  } catch {
    reportFailure(null)
  }
}

function onCropped(rect: CropRect) {
  const dataUrl = pendingDataUrl.value
  pendingDataUrl.value = null
  if (dataUrl) void encodeAndUpload(dataUrl, rect)
}

function onCropCancelled() {
  pendingDataUrl.value = null
}

async function encodeAndUpload(dataUrl: string, crop?: CropRect) {
  try {
    const prepared = await prepareImage(dataUrl, captureOptions.value, crop)

    localPreview.value = prepared.dataUrl
    isUploading.value = true

    const previous = props.modelValue
    const image = await uploadImage({
      blob: prepared.blob,
      purpose: props.purpose,
      width: prepared.width,
      height: prepared.height,
      avgColor: prepared.avgColor,
      householdId: props.householdId,
    })
    emit("update:modelValue", image.id)
    emit("update:avgColor", image.avgColor ?? prepared.avgColor)

    // Free the quota straight away rather than waiting for the nightly sweep
    // to notice the old photo is unreferenced.
    if (props.deleteReplaced && previous && previous !== image.id) {
      deleteImage(previous).catch(() => {
        /* the GC cron collects it */
      })
    }
  } catch (error) {
    reportFailure(error)
  } finally {
    isUploading.value = false
  }
}

function reportFailure(error: unknown) {
  localPreview.value = null
  const message = error instanceof Error ? error.message : ""
  if (message.includes("quota_exceeded")) {
    toast.error(t("images.quota_exceeded"))
  } else if (!navigator.onLine) {
    // Uploads can't be queued in the offline outbox (a File doesn't survive
    // localStorage), so this fails closed and says so.
    toast.error(t("images.offline"))
  } else {
    toast.error(t("images.upload_failed"))
  }
}

function clear() {
  const previous = props.modelValue
  localPreview.value = null
  emit("update:modelValue", null)
  emit("update:avgColor", null)
  if (props.deleteReplaced && previous) {
    deleteImage(previous).catch(() => {
      /* the GC cron collects it */
    })
  }
}

onUnmounted(() => {
  localPreview.value = null
})
</script>
